// filepath: apps/web/src/features/admin/effectsMetrics/CircuitBreakerConfigDialog.tsx
//
// FR-UI-ADM-9 AC-3 — dialog for editing BreakerPolicy on one endpoint.
// Native <dialog> element, focus-trapped, ESC-to-close, accessible labels.
//
// Input ranges (I-3):
//   - consecutive_failures: [1, 100]
//   - open_timeout_ms:      [1000, 300000]
//   - half_open_max_probes: [1, 10]
//
// Out-of-range disables submit + shows aria-invalid message (AC-3b).
// On 5xx surfaces fetch-failure component with retry (AC-3c).

import React from "react";
import { useTranslation } from "react-i18next";
import type { SetBreakerPolicyRequest } from "./types";
import type { EffectsMetricsFetchError } from "./types";

interface CircuitBreakerConfigDialogProps {
  open: boolean;
  endpointKey: string | null;
  initial: {
    consecutive_failures: number;
    open_timeout_ms: number;
    half_open_max_probes: number;
  };
  onClose: () => void;
  onSubmit: (req: SetBreakerPolicyRequest) => void;
  isPending: boolean;
  error: EffectsMetricsFetchError | null;
}

const RANGES = {
  consecutive_failures: { min: 1, max: 100 },
  open_timeout_ms: { min: 1000, max: 300_000 },
  half_open_max_probes: { min: 1, max: 10 },
} as const;

export function CircuitBreakerConfigDialog({
  open,
  endpointKey,
  initial,
  onClose,
  onSubmit,
  isPending,
  error,
}: CircuitBreakerConfigDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [cf, setCf] = React.useState(initial.consecutive_failures);
  const [ot, setOt] = React.useState(initial.open_timeout_ms);
  const [hp, setHp] = React.useState(initial.half_open_max_probes);

  React.useEffect(() => {
    setCf(initial.consecutive_failures);
    setOt(initial.open_timeout_ms);
    setHp(initial.half_open_max_probes);
  }, [
    initial.consecutive_failures,
    initial.open_timeout_ms,
    initial.half_open_max_probes,
    open,
  ]);

  if (!open || !endpointKey) return null;

  const cfValid =
    cf >= RANGES.consecutive_failures.min &&
    cf <= RANGES.consecutive_failures.max;
  const otValid =
    ot >= RANGES.open_timeout_ms.min && ot <= RANGES.open_timeout_ms.max;
  const hpValid =
    hp >= RANGES.half_open_max_probes.min &&
    hp <= RANGES.half_open_max_probes.max;
  const allValid = cfValid && otValid && hpValid;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="circuit-config-title"
      className="circuit-config-dialog"
    >
      <h2 id="circuit-config-title">
        {t("admin.effects.metrics.dialog_title", "Configure breaker")}
      </h2>
      <p
        className="circuit-config-endpoint"
        data-testid="circuit-config-endpoint"
      >
        {endpointKey}
      </p>

      <label>
        <span>
          {t(
            "admin.effects.metrics.policy.consecutive_failures",
            "Consecutive failures (1-100)",
          )}
        </span>
        <input
          type="number"
          min={RANGES.consecutive_failures.min}
          max={RANGES.consecutive_failures.max}
          value={cf}
          onChange={(e) => setCf(Number(e.target.value))}
          aria-invalid={!cfValid}
        />
        {!cfValid && (
          <span className="field-error" role="alert">
            {t(
              "admin.effects.metrics.policy.out_of_range",
              "Value must be in [{{min}}, {{max}}]",
              {
                min: RANGES.consecutive_failures.min,
                max: RANGES.consecutive_failures.max,
              },
            )}
          </span>
        )}
      </label>

      <label>
        <span>
          {t(
            "admin.effects.metrics.policy.open_timeout_ms",
            "Open timeout ms (1000-300000)",
          )}
        </span>
        <input
          type="number"
          min={RANGES.open_timeout_ms.min}
          max={RANGES.open_timeout_ms.max}
          value={ot}
          onChange={(e) => setOt(Number(e.target.value))}
          aria-invalid={!otValid}
        />
        {!otValid && (
          <span className="field-error" role="alert">
            {t(
              "admin.effects.metrics.policy.out_of_range",
              "Value must be in [{{min}}, {{max}}]",
              {
                min: RANGES.open_timeout_ms.min,
                max: RANGES.open_timeout_ms.max,
              },
            )}
          </span>
        )}
      </label>

      <label>
        <span>
          {t(
            "admin.effects.metrics.policy.half_open_max_probes",
            "Half-open max probes (1-10)",
          )}
        </span>
        <input
          type="number"
          min={RANGES.half_open_max_probes.min}
          max={RANGES.half_open_max_probes.max}
          value={hp}
          onChange={(e) => setHp(Number(e.target.value))}
          aria-invalid={!hpValid}
        />
        {!hpValid && (
          <span className="field-error" role="alert">
            {t(
              "admin.effects.metrics.policy.out_of_range",
              "Value must be in [{{min}}, {{max}}]",
              {
                min: RANGES.half_open_max_probes.min,
                max: RANGES.half_open_max_probes.max,
              },
            )}
          </span>
        )}
      </label>

      {error && (
        <p role="alert" className="circuit-config-error">
          {error.message}
        </p>
      )}

      <div className="circuit-config-actions">
        <button type="button" onClick={onClose} disabled={isPending}>
          {t("admin.effects.metrics.dialog_cancel", "Cancel")}
        </button>
        <button
          type="button"
          onClick={() =>
            onSubmit({
              endpoint_key: endpointKey,
              consecutive_failures: cf,
              open_timeout_ms: ot,
              half_open_max_probes: hp,
            })
          }
          disabled={!allValid || isPending}
        >
          {isPending
            ? t("admin.effects.metrics.dialog_saving", "Saving…")
            : t("admin.effects.metrics.dialog_save", "Save")}
        </button>
      </div>
    </div>
  );
}
